// D9-06 (#408) — money is integer cents end to end. This module is the ONLY
// place a Service money value is converted between a typed decimal STRING
// and integer cents; every other Service file passes `_cents` values
// straight through without doing arithmetic on them (A-9, D6-04).
//
// `formatCents` reproduces the hand-rolled formatter in
// apps/tenant/resources/views/pdf/service-invoice.blade.php:113-117 byte for
// byte, INCLUDING its four-entry currency symbol map, so the screen and the
// printed invoice never disagree (#947). The browser's built-in currency
// formatter is deliberately NOT used here: formatting CAD through it renders
// `CA$`, but the Blade template's own map renders `C$` — reusing the
// built-in formatter would silently diverge the on-screen figure from what
// the customer's invoice actually prints.
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  CAD: 'C$',
  GBP: '£',
  EUR: '€',
};

/**
 * Formats integer cents as a currency string matching the Blade PDF
 * formatter exactly. Returns an em-dash for `null`/`undefined` so callers
 * never each invent their own empty-state.
 */
export function formatCents(cents: number | null | undefined, currencyCode = 'USD'): string {
  if (cents === null || cents === undefined) {
    return '—';
  }

  const symbol = CURRENCY_SYMBOLS[currencyCode] ?? `${currencyCode} `;
  const sign = cents < 0 ? '-' : '';
  const amount = (Math.abs(cents) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${sign}${symbol}${amount}`;
}

/**
 * Parses a typed decimal string into integer cents. Splits the trimmed
 * string on `.` and pads/truncates the fractional part to exactly two
 * digits by STRING concatenation — it never multiplies a float by 100
 * (D9-06's explicit anti-pattern: rounding `1.15` scaled to cents drifts to
 * `114.99999999999999` under IEEE-754 before the round). Returns `null` for
 * an empty or unparseable string.
 */
export function parseDecimalToCents(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  // Strip a leading currency symbol and grouping separators — anything that
  // isn't a digit, a decimal point or a leading minus sign.
  const cleaned = trimmed.replace(/[^0-9.-]/g, '');
  if (!/\d/.test(cleaned)) {
    return null;
  }

  const negative = cleaned.startsWith('-');
  const unsigned = negative ? cleaned.slice(1) : cleaned;
  const [wholePartRaw, fracPartRaw = ''] = unsigned.split('.');
  const wholePart = wholePartRaw || '0';

  if (!/^\d+$/.test(wholePart) || !/^\d*$/.test(fracPartRaw)) {
    return null;
  }

  // Pad or truncate to exactly two fractional digits, then concatenate the
  // whole and fractional digit strings directly into the cents value — no
  // multiplication, no division, no float in the loop.
  const centsFraction = `${fracPartRaw}00`.slice(0, 2);
  const cents = Number(`${wholePart}${centsFraction}`);

  if (!Number.isFinite(cents)) {
    return null;
  }

  return negative ? -cents : cents;
}

/**
 * The inverse of `parseDecimalToCents`, used to seed a money input's
 * display value on edit. Returns `''` for a nullish value.
 */
export function centsToDecimalString(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) {
    return '';
  }

  const negative = cents < 0;
  const abs = Math.abs(cents);
  const whole = Math.trunc(abs / 100);
  const fraction = String(abs % 100).padStart(2, '0');

  return `${negative ? '-' : ''}${whole}.${fraction}`;
}
